/**
 * Memorial music link helpers — platform labels, embed detection, external notes.
 */

export const MUSIC_EXTERNAL_NOTE = 'Opens on the original source.';

export const MUSIC_PLATFORMS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'spotify', label: 'Spotify' },
  { id: 'apple_music', label: 'Apple Music' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'vimeo', label: 'Vimeo' },
  { id: 'other', label: 'Other' },
];

export const MUSIC_PLATFORM_CONFIG = {
  youtube: { label: 'YouTube', mark: 'YT', markClass: 'bg-red-600 text-white' },
  spotify: { label: 'Spotify', mark: 'Sp', markClass: 'bg-[#1DB954] text-white' },
  apple_music: { label: 'Apple Music', mark: 'AM', markClass: 'bg-gradient-to-br from-pink-500 to-red-500 text-white' },
  soundcloud: { label: 'SoundCloud', mark: 'SC', markClass: 'bg-[#ff5500] text-white' },
  vimeo: { label: 'Vimeo', mark: 'Vi', markClass: 'bg-sky-600 text-white' },
  other: { label: 'Music link', mark: '♪', markClass: 'bg-[#8b7355] text-white' },
};

const AUDIO_EXT = /\.(mp3|m4a|wav|ogg|webm)(\?|$)/i;

export function detectMusicPlatform(url) {
  const u = (url || '').toLowerCase();
  if (/youtu(\.be|be\.com)/.test(u)) return 'youtube';
  if (/spotify\.com/.test(u)) return 'spotify';
  if (/music\.apple\.com|itunes\.apple\.com/.test(u)) return 'apple_music';
  if (/soundcloud\.com/.test(u)) return 'soundcloud';
  if (/vimeo\.com/.test(u)) return 'vimeo';
  return 'other';
}

export function isDirectAudioUrl(url) {
  return AUDIO_EXT.test(url || '');
}

export function getMusicPlatformLabel(platform) {
  return MUSIC_PLATFORM_CONFIG[platform]?.label || MUSIC_PLATFORM_CONFIG.other.label;
}

export function newMusicLink(overrides = {}) {
  return {
    id: `music-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: '',
    artist: '',
    link_url: '',
    platform: 'other',
    note: '',
    is_featured: false,
    sort_order: 0,
    visible_publicly: true,
    ...overrides,
  };
}

export function newVideoLinkItem(overrides = {}) {
  return {
    id: `video-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: '',
    video_url: '',
    platform: 'external',
    description: '',
    visible_publicly: true,
    sort_order: 0,
    ...overrides,
  };
}

export function normalizeMusicLinksForForm(memorial) {
  const rows = memorial?.music_links;
  if (Array.isArray(rows) && rows.length) {
    return rows.map((item, i) => ({
      ...newMusicLink(),
      ...item,
      sort_order: item.sort_order ?? i,
      platform: item.platform || detectMusicPlatform(item.link_url),
    }));
  }
  if (memorial?.soundtrack_enabled && memorial?.soundtrack_url) {
    return [
      newMusicLink({
        id: 'legacy-soundtrack',
        title: memorial.soundtrack_title || 'Memorial soundtrack',
        link_url: memorial.soundtrack_url,
        note: memorial.soundtrack_note || '',
        platform: detectMusicPlatform(memorial.soundtrack_url),
        is_featured: true,
        sort_order: 0,
      }),
    ];
  }
  return [];
}

export function normalizeVideoLinksForForm(links) {
  if (!Array.isArray(links)) return [];
  return links.map((item, i) => {
    if (typeof item === 'string') {
      return newVideoLinkItem({ video_url: item, sort_order: i });
    }
    const url = item.video_url || item.url || '';
    return newVideoLinkItem({
      ...item,
      video_url: url,
      description: item.description || item.video_notes || '',
      platform: item.platform || item.video_provider || detectMusicPlatform(url),
      sort_order: item.sort_order ?? i,
    });
  });
}

export function musicLinksPayload(links) {
  return (links || [])
    .filter((m) => (m.link_url || '').trim().startsWith('http'))
    .map((m, i) => ({
      id: m.id,
      title: (m.title || '').trim() || 'Untitled song',
      artist: (m.artist || '').trim() || null,
      link_url: m.link_url.trim(),
      platform: m.platform || detectMusicPlatform(m.link_url),
      note: (m.note || '').trim() || null,
      is_featured: Boolean(m.is_featured),
      sort_order: i,
      visible_publicly: m.visible_publicly !== false,
    }));
}

export function videoLinksPayload(links) {
  return (links || [])
    .filter((v) => (v.video_url || '').trim().startsWith('http'))
    .map((v, i) => ({
      id: v.id,
      title: (v.title || '').trim() || null,
      video_url: v.video_url.trim(),
      description: (v.description || '').trim() || null,
      visible_publicly: v.visible_publicly !== false,
      sort_order: i,
    }));
}
